import React, { useEffect } from 'react';
import { GlassCard } from './reports/_shared/GlassCard';
import { Footer } from './reports/_shared/Footer';
import {
    Eye, TrendingUp, Users, Heart, PlayCircle, Radio,
    Layers, BadgeCheck, Workflow, Lock, ArrowRight, ExternalLink,
} from 'lucide-react';

/*
 * SocialSEO — Case Studies / Proof Wall
 * Route: /case-studies
 *
 * EVERY number on this page is lifted directly from a real analytics
 * screenshot in /public/case-studies. Nothing is projected or invented.
 * Source screenshots live in the repo root "Aura screenshots" folder.
 *
 * Deliberately EXCLUDED: Kush Desai / Source Code Lab analytics. That work
 * was founder content production, not fan-page marketing + PR, so it does
 * not belong in a fan-page case-study page.
 */

// ───────────────────────────── primitives ─────────────────────────────

const Eyebrow = ({ children }) => (
    <p className="text-white/40 text-xs uppercase tracking-[0.25em] font-semibold mb-3">{children}</p>
);

const Stat = ({ icon: Icon, value, label, accent = 'text-purple-300' }) => (
    <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
            {Icon && <Icon size={16} className={accent} />}
            <span className="text-3xl md:text-4xl font-black text-white tracking-tight">{value}</span>
        </div>
        <span className="text-white/50 text-xs md:text-sm font-medium leading-snug">{label}</span>
    </div>
);

const Shot = ({ src, caption, tall = false }) => (
    <a
        href={src}
        target="_blank"
        rel="noopener noreferrer"
        className="group block rounded-2xl overflow-hidden border border-white/10 bg-black/30 backdrop-blur-md transition-transform duration-300 hover:scale-[1.02]"
    >
        <div className={`relative ${tall ? 'aspect-[9/16]' : ''} overflow-hidden`}>
            <img
                src={src}
                alt={caption}
                loading="lazy"
                className={`w-full ${tall ? 'h-full object-cover object-top' : 'h-auto'} block`}
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                <ExternalLink size={20} className="text-white/0 group-hover:text-white/80 transition-colors duration-300" />
            </div>
        </div>
        {caption && (
            <p className="text-white/45 text-xs px-3 py-2 font-mono">{caption}</p>
        )}
    </a>
);

// ───────────────────────────── page ─────────────────────────────

export default function CaseStudies() {
    useEffect(() => {
        document.title = 'Case Studies | SocialSEO';
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white relative" style={{ fontFamily: "'Plus Jakarta Sans', Inter, sans-serif" }}>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');`}</style>

            {/* Nav */}
            <header className="fixed top-0 left-0 right-0 z-50 p-4 md:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
                    <span className="font-bold tracking-tight text-purple-300"><span className="text-white">Social</span>SEO</span>
                </div>
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
                    <span className="text-sm font-medium text-white/80">Case Studies</span>
                </div>
            </header>

            <div className="relative z-10">
                <main>

                    {/* ───────────── HERO ───────────── */}
                    <section className="min-h-[80vh] flex flex-col justify-center items-center text-center px-4 pt-28 pb-12">
                        <div className="max-w-5xl mx-auto w-full space-y-6">
                            <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 backdrop-blur-md">
                                <span className="w-2 h-2 rounded-full bg-emerald-400 mr-2 animate-pulse" />
                                <span className="text-sm font-medium text-emerald-300 tracking-wide">Real screenshots. Real numbers.</span>
                            </div>

                            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-white via-purple-100 to-white drop-shadow-lg leading-tight">
                                Fan-page marketing<br />that moves real numbers
                            </h1>

                            <p className="text-lg md:text-xl text-purple-200/70 max-w-2xl mx-auto leading-relaxed">
                                We build and run networks of fan pages and PR distribution that put brands in front of
                                <span className="text-white font-semibold"> hundreds of millions of viewers</span>. Every figure below
                                comes straight from the live analytics dashboard, not a slide.
                            </p>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-12">
                                <GlassCard className="min-h-[120px] flex items-center"><Stat icon={Eye} value="373M" label="ECL views, single 30-day window" accent="text-blue-300" /></GlassCard>
                                <GlassCard className="min-h-[120px] flex items-center"><Stat icon={PlayCircle} value="31.8M" label="Badshah fan-channel views" accent="text-purple-300" /></GlassCard>
                                <GlassCard className="min-h-[120px] flex items-center"><Stat icon={Radio} value="1.9M" label="TVK views, one page" accent="text-emerald-300" /></GlassCard>
                                <GlassCard className="min-h-[120px] flex items-center"><Stat icon={TrendingUp} value="100%" label="Organic, zero ad spend" accent="text-amber-300" /></GlassCard>
                            </div>
                        </div>
                    </section>

                    {/* ───────────── CASE 1 · BADSHAH ───────────── */}
                    <section className="py-16 px-4">
                        <div className="container mx-auto max-w-6xl">
                            <Eyebrow>Case Study 01 · Music / Entertainment</Eyebrow>
                            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
                                Badshah <span className="text-white/40 font-medium text-2xl md:text-3xl">· 31.8M views from one fan channel</span>
                            </h2>
                            <p className="text-white/60 max-w-3xl mb-10 leading-relaxed">
                                A fan-page YouTube channel built from zero, distributing short-form clips of Badshah's interviews
                                and podcast moments. Live since September 2025. The channel is public, so every number is independently verifiable.
                            </p>

                            <GlassCard className="mb-8">
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                                    <Stat icon={Eye} value="31.8M" label="Lifetime views" />
                                    <Stat icon={PlayCircle} value="17.3M" label="Engaged views" accent="text-blue-300" />
                                    <Stat icon={Heart} value="532.7K" label="Likes" accent="text-rose-300" />
                                    <Stat icon={TrendingUp} value="8.2M" label="Views, last 90 days" accent="text-emerald-300" />
                                    <Stat icon={Users} value="77.9%" label="Stayed to watch" accent="text-amber-300" />
                                    <Stat icon={Layers} value="42" label="Videos published" accent="text-purple-300" />
                                </div>
                            </GlassCard>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <Shot src="/case-studies/badshah-lifetime.png" caption="YouTube Studio · 31,770,467 lifetime views" />
                                <Shot src="/case-studies/badshah-engaged.png" caption="365-day · 31.8M views, 17.3M engaged, 532.7K likes" />
                                <Shot src="/case-studies/badshah-90d.png" caption="Last 90 days · 8.2M views" />
                                <Shot src="/case-studies/badshah-channel.png" caption="BadshahDaily · 42 videos, 31.7M channel views" />
                            </div>
                        </div>
                    </section>

                    {/* ───────────── CASE 2 · ECL ───────────── */}
                    <section className="py-16 px-4">
                        <div className="container mx-auto max-w-6xl">
                            <Eyebrow>Case Study 02 · Live Event / Sports Entertainment</Eyebrow>
                            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
                                Entertainers Cricket League <span className="text-white/40 font-medium text-2xl md:text-3xl">· 373M views in 30 days</span>
                            </h2>
                            <p className="text-white/60 max-w-3xl mb-10 leading-relaxed">
                                A full content and distribution engine around a creator cricket league in Delhi. In a single 30-day
                                window the network pushed the brand to nearly 40 million accounts and grew the following by more than seven times.
                            </p>

                            <GlassCard className="mb-8">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                    <Stat icon={Eye} value="373M" label="Views, 30 days" accent="text-blue-300" />
                                    <Stat icon={Users} value="39.3M" label="Accounts reached, +1,312%" accent="text-emerald-300" />
                                    <Stat icon={TrendingUp} value="+722%" label="Follower growth" accent="text-amber-300" />
                                    <Stat icon={PlayCircle} value="75.8%" label="Reach from Reels" accent="text-purple-300" />
                                </div>
                            </GlassCard>

                            <div className="max-w-3xl mx-auto">
                                <Shot src="/case-studies/ecl-overview.jpeg" caption="@eclt10 insights · 373M views, 39.3M reached, +722% followers (Aug 25 to Sep 23)" />
                            </div>
                        </div>
                    </section>

                    {/* ───────────── CASE 3 · TVK ───────────── */}
                    <section className="py-16 px-4">
                        <div className="container mx-auto max-w-6xl">
                            <Eyebrow>Case Study 03 · Political / Regional</Eyebrow>
                            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
                                TVK Political Reels Engine <span className="text-white/40 font-medium text-2xl md:text-3xl">· consistent 100K to 1.9M drops</span>
                            </h2>
                            <p className="text-white/60 max-w-3xl mb-10 leading-relaxed">
                                A network of Tamil political fan pages built around real-time news and campaign moments. Multi-platform
                                across Facebook and YouTube, the pages turn out six-figure-view reels week after week with high share velocity.
                            </p>

                            <GlassCard className="mb-8">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                    <Stat icon={Eye} value="1.9M" label="Views, one page (2 months)" accent="text-blue-300" />
                                    <Stat icon={PlayCircle} value="1.23M" label="YouTube views, 90 days" accent="text-purple-300" />
                                    <Stat icon={Heart} value="47.6K" label="Interactions, single page" accent="text-rose-300" />
                                    <Stat icon={Radio} value="100K+" label="Per-reel views, repeatable" accent="text-emerald-300" />
                                </div>
                            </GlassCard>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <Shot src="/case-studies/tvk-tharkuri-fb.png" caption="Facebook page insights · 1,901,627 views, 47.6K interactions" />
                                <Shot src="/case-studies/tvk-tharkuri-yt.png" caption="YouTube · 1,234,278 views in 90 days" />
                                <Shot src="/case-studies/tvk-vijaytakeslab-fb.jpeg" caption="Single post · 147,055 views, 2,123 interactions" />
                                <Shot src="/case-studies/tvk-tharkuri-post1.jpeg" caption="Single reel · 131,433 views" />
                                <Shot src="/case-studies/tvk-tharkuri-post2.jpeg" caption="News-jacked reel · 126,555 views" />
                            </div>
                        </div>
                    </section>

                    {/* ───────────── PLAYBOOK ───────────── */}
                    <section className="py-16 px-4">
                        <div className="container mx-auto max-w-5xl">
                            <Eyebrow>How we do it</Eyebrow>
                            <h2 className="text-3xl md:text-4xl font-bold text-white mb-10">The fan-page distribution playbook</h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <GlassCard><div className="flex flex-col gap-3"><div className="bg-purple-500/20 text-purple-300 p-3 rounded-xl inline-flex w-fit"><Layers size={22} /></div><h3 className="text-lg font-bold text-white">Network, not a single page</h3><p className="text-white/55 text-sm leading-relaxed">Themed fan pages across Instagram, YouTube, Facebook, X and TikTok. One source of content, many surfaces, multiplied reach.</p></div></GlassCard>
                                <GlassCard><div className="flex flex-col gap-3"><div className="bg-blue-500/20 text-blue-300 p-3 rounded-xl inline-flex w-fit"><Workflow size={22} /></div><h3 className="text-lg font-bold text-white">Tiered promotion engine</h3><p className="text-white/55 text-sm leading-relaxed">Tier-3 experimental pages test hooks, winners get promoted to Tier-2 and Tier-1. The network compounds month over month.</p></div></GlassCard>
                                <GlassCard><div className="flex flex-col gap-3"><div className="bg-emerald-500/20 text-emerald-300 p-3 rounded-xl inline-flex w-fit"><BadgeCheck size={22} /></div><h3 className="text-lg font-bold text-white">100% organic, fully managed</h3><p className="text-white/55 text-sm leading-relaxed">Logins, posting, captions, replies, reporting. Every page built, run and owned for you. Zero ad spend, zero bots.</p></div></GlassCard>
                            </div>
                        </div>
                    </section>

                    {/* ───────────── NDA NOTE ───────────── */}
                    <section className="py-12 px-4">
                        <div className="container mx-auto max-w-3xl">
                            <GlassCard>
                                <div className="flex items-start gap-4">
                                    <div className="bg-white/10 text-white/70 p-3 rounded-xl inline-flex flex-shrink-0"><Lock size={20} /></div>
                                    <div>
                                        <h3 className="text-lg font-bold text-white mb-1">More results under NDA</h3>
                                        <p className="text-white/55 text-sm leading-relaxed">
                                            We also run brands we cannot name publicly, including a UAE fitness brand. Happy to walk you
                                            through those numbers live on a call.
                                        </p>
                                    </div>
                                </div>
                            </GlassCard>
                        </div>
                    </section>

                    {/* ───────────── CTA ───────────── */}
                    <section className="py-20 px-4">
                        <div className="container mx-auto max-w-3xl text-center">
                            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">Want these numbers for your brand?</h2>
                            <p className="text-white/60 mb-8 max-w-xl mx-auto">Let's map a fan-page network to your content and put it to work.</p>
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                <a href="https://calendly.com/personalbrand-wasnot/15-minutes-discovery-call" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white text-black font-bold px-7 py-3.5 rounded-full hover:bg-white/90 transition-colors">
                                    Book a discovery call <ArrowRight size={18} />
                                </a>
                                <a href="mailto:suresh@socialseo.in?subject=Fan-page%20marketing%2C%20let%27s%20talk" className="inline-flex items-center gap-2 text-white/70 font-medium px-5 py-3.5 rounded-full border border-white/15 hover:bg-white/5 transition-colors">
                                    suresh@socialseo.in
                                </a>
                            </div>
                        </div>
                    </section>

                    <Footer />
                </main>
            </div>
        </div>
    );
}
